/**
 * Document renderer (Library / file-preview).
 *
 * Renders a `kind: "document"` artifact through the exact same
 * {@link DocumentArtifact} the chat artifact canvas uses — TipTap with the
 * shared markdown extension set and `artifact-content` typography — so a
 * document opened from the Library matches the canvas one-to-one instead of a
 * separate ReactMarkdown pass. This path is read-only.
 *
 * Own scroll container: `DocumentArtifact` centers its own reading-width
 * column (`mx-auto max-w-4xl`), so the scrollbar sits at this container's
 * right edge (the modal edge), not mid-canvas.
 */
import { Loader2 } from "lucide-react";

import { DocumentArtifact } from "@/components/artifacts/document-artifact";

import { useFileText } from "../hooks/use-file-text";
import type { FileRendererProps } from "../types";

export function DocumentSourceRenderer({ source }: FileRendererProps) {
  const state = useFileText(source);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Couldn&apos;t load this document.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background">
      <DocumentArtifact content={state.text} editable={false} />
    </div>
  );
}
